import { ClubCrudApi } from '@hefe/store';
import React from 'react';
import CustomForm, {
  FormField,
} from '../../../core/components/form/custom-form';
import {
  CrudConfig,
  CrudEntity,
  getFormFieldsWithValues,
} from '../../models/club-crud.model';

interface ClubCrudFormProps<T extends CrudEntity> {
  config: CrudConfig<T>;
  item?: T;
  formId: string;
  onSuccess?: () => void;
  onLoadingChange?: (loading: boolean) => void;
}

export default function ClubCrudForm<T extends CrudEntity>({
  config,
  item,
  formId,
  onSuccess,
  onLoadingChange,
}: ClubCrudFormProps<T>) {
  const [createItem, { isLoading: isCreating }] =
    ClubCrudApi.useCreateMutation();
  const [updateItem, { isLoading: isUpdating }] =
    ClubCrudApi.useUpdateMutation();

  const isEditing = !!item;
  const isLoading = isCreating || isUpdating;

  // Notify parent about loading state changes
  React.useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  // Get form fields - use editFormFields if editing and available, otherwise formFields
  const baseFields = isEditing
    ? config.editFormFields ?? config.formFields
    : config.formFields;

  // If editing, populate with item values
  const fields: FormField[] =
    isEditing && item ? getFormFieldsWithValues(baseFields, item) : baseFields;

  const onSubmit = async (values: Partial<T>) => {
    try {
      if (isEditing && item) {
        await updateItem({
          resource: config.resource,
          data: { ...values, id: item.id } as any,
        }).unwrap();
      } else {
        await createItem({
          resource: config.resource,
          data: values,
        }).unwrap();
      }

      onSuccess?.();
    } catch (error) {
      // Error handling is done by RTK Query middleware
    }
  };

  return (
    <CustomForm
      formId={formId}
      fields={fields}
      onSubmit={onSubmit}
      loading={isLoading}
    />
  );
}
